
// Utility functions for crime marker styling

/**
 * Get the color for a crime category marker
 */
export const getCrimeMarkerColor = (category: string, isRecent: boolean): string => {
  // Enhanced color scheme for crime categories
  if (isRecent) {
    switch (category) {
      case "Sexual Harassment":
        return "#FF2D55"; // Bright red
      case "Civic Disorder":
        return "#5856D6"; // Purple
      case "Drug Dealing":
        return "#34C759"; // Bright green
      default:
        return "#FF9500"; // Orange for unknown
    }
  }
  
  // Slightly muted colors for older reports
  switch (category) {
    case "Sexual Harassment":
      return "#FF3B30"; // Red
    case "Civic Disorder":
      return "#007AFF"; // Blue
    case "Drug Dealing":
      return "#30D158"; // Green
    default:
      return "#FF9500"; // Orange for unknown
  }
};

/**
 * Get the Google Maps marker icon for a crime report
 */
export const getGoogleMarkerIcon = (category: string, isRecent: boolean) => {
  if (!window.google || !window.google.maps) return null;
  
  const color = getCrimeMarkerColor(category, isRecent);
  const scale = isRecent ? 1.2 : 1;
  
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 0.8,
    strokeColor: 'white',
    strokeWeight: 2,
    scale: 10 * scale
  };
};

/**
 * Generate HTML content for the Google Maps info window
 */
export const generateInfoWindowContent = (
  category: string, 
  created_at: string, 
  location_name: string | null, 
  latitude: number, 
  longitude: number,
  description: string | null, 
  image_paths: string[] | null,
  isRecent: boolean
): string => {
  const color = getCrimeMarkerColor(category, isRecent);
  
  return `
    <div style="padding: 12px; max-width: 300px; font-family: 'Inter', sans-serif;">
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></div>
        <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: ${color};">
          ${category}
        </h3>
      </div>
      <p style="margin: 6px 0; font-size: 14px;"><strong>Date:</strong> ${new Date(created_at).toLocaleString()}</p>
      <p style="margin: 6px 0; font-size: 14px;"><strong>Location:</strong> ${location_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}</p>
      ${description ? `<p style="margin: 6px 0; font-size: 14px;"><strong>Description:</strong> ${description}</p>` : ''}
      ${isRecent ? '<p style="margin: 8px 0; font-weight: bold; font-size: 14px; color: #FF2D55; padding: 4px 8px; background-color: rgba(255, 45, 85, 0.1); border-radius: 4px;">Reported in the last 24 hours</p>' : ''}
      ${image_paths && image_paths.length > 0 ? 
        `<p style="margin: 6px 0; font-size: 14px;"><strong>Evidence:</strong> ${image_paths.length} image(s) attached</p>` : ''}
    </div>
  `;
};
